import type { WeaponStats } from "../combat/weapons.js";
import type { AmmoItemDef } from "./types.js";

/**
 * Gun + cartridge = what leaves the barrel. Handling numbers live on the
 * weapon, TERMINAL numbers (damage, penetration, velocity, pellets) on the
 * cartridge; this is the single place they are folded. The fields are
 * `WeaponStats`', so an `EffectiveWeapon` passes anywhere a weapon's stats did;
 * `pellets` is the one addition.
 */
export interface EffectiveWeapon extends WeaponStats {
  /**
   * Projectiles per trigger pull: 1 for everything but buckshot (8 independent
   * rounds, each with the full `damage`).
   */
  pellets: number;
}

/**
 * Folds `ammo` over `w`. `w` is a `WeaponStats`, so the numbers may be COMPUTED
 * — `weaponWithAmmo(aggregateWeaponStats(def, item), ammoDef)` — and a plain
 * `def.stats` still satisfies it. `ammo === null` uses the weapon's own
 * terminals with `pellets` 1 (back-compatibility, not an error). Pure.
 */
export function weaponWithAmmo(w: WeaponStats, ammo: AmmoItemDef | null): EffectiveWeapon {
  return {
    rpm: w.rpm,
    magSize: w.magSize,
    reloadSec: w.reloadSec,
    muzzleVelocity: ammo === null ? w.muzzleVelocity : w.muzzleVelocity * ammo.velocityMult,
    damage: ammo === null ? w.damage : ammo.damage,
    penPower: ammo === null ? w.penPower : ammo.penPower,
    falloffStartM: w.falloffStartM,
    falloffEndM: w.falloffEndM,
    falloffMinMult: w.falloffMinMult,
    ergonomics: w.ergonomics,
    adsTimeSec: w.adsTimeSec,
    hipSpreadDeg: w.hipSpreadDeg,
    zeroRangeM: w.zeroRangeM,
    pellets: ammo === null ? 1 : ammo.pellets,
  };
}
